import Layer from "ol/layer/Layer";
import ImageState from "ol/ImageState";
import { SPEED_BREAKPOINTS } from "./DirectionalLayers";
import { interpolatedSpeedFromColor } from "./style/util/colorScale";
import {
  bandIndexForSpeed,
  directionHeadingRadians,
  speedBands,
  stripeAxis,
  stripeProfile,
  stripeSpeedForKmh,
  wrap,
} from "./directionalMotionGeometry.mjs";

export const MOTION_COLOR = "#1e5aff";
export const MOTION_OPACITY = 0.6;

// CSS pixels / CSS pixels per second
const STRIPE_SPACING = 28;
const STRIPE_HEAD = 6;
const STRIPE_SPEED_AT_50_KMH = 35;
const STRIPE_MIN_SPEED = 4;
const PROFILE_SAMPLES_PER_PX = 4;

const BANDS = speedBands(SPEED_BREAKPOINTS);
const BAND_PX_PER_SEC = BANDS.map((band) =>
  stripeSpeedForKmh(band.kmh, STRIPE_SPEED_AT_50_KMH, STRIPE_MIN_SPEED),
);
const PROFILE = stripeProfile(
  STRIPE_SPACING,
  STRIPE_HEAD,
  PROFILE_SAMPLES_PER_PX,
);

// Recolors a directional FCD layer to a single color. Each layer has its own
// canvas (unique className), so `source-atop` only paints that layer's pixels.
export function attachMotionRecolor(layer, isMotionMode) {
  layer.on("postrender", function (event) {
    if (!isMotionMode() || layer.getOpacity() === 0) return;
    const context = event.context;
    context.save();
    context.globalCompositeOperation = "source-atop";
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.fillStyle = MOTION_COLOR;
    context.fillRect(0, 0, context.canvas.width, context.canvas.height);
    context.restore();
  });
}

const speedByColor = new Map();
function speedForColor(r, g, b) {
  const key = (r << 16) | (g << 8) | b;
  let speed = speedByColor.get(key);
  if (speed === undefined) {
    speed = interpolatedSpeedFromColor([r, g, b], SPEED_BREAKPOINTS);
    speedByColor.set(key, speed);
  }
  return speed;
}

// Per loaded WMS speed image: its non-transparent pixels with their speed band,
// sampled at ~CSS resolution (every `step`-th pixel on hidpi images). Built
// once per image; the per-frame loop only touches these pixels.
const roadPixelsCache = new WeakMap();
function getRoadPixels(image) {
  let cached = roadPixelsCache.get(image);
  if (cached) return cached;
  const img = image.getImage();
  const step = Math.max(1, Math.round(image.getPixelRatio()));
  const width = Math.floor(img.width / step);
  const height = Math.floor(img.height / step);
  if (width === 0 || height === 0) return null;

  const canvas = document.createElement("canvas");
  canvas.width = img.width;
  canvas.height = img.height;
  const context = canvas.getContext("2d", { willReadFrequently: true });
  context.drawImage(img, 0, 0);
  const rgba = context.getImageData(0, 0, img.width, img.height).data;

  const xs = [];
  const ys = [];
  const alphas = [];
  const bands = [];
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * step * img.width + x * step) * 4;
      const alpha = rgba[i + 3];
      if (alpha === 0) continue;
      xs.push(x);
      ys.push(y);
      alphas.push(alpha);
      bands.push(
        bandIndexForSpeed(
          BANDS,
          speedForColor(rgba[i], rgba[i + 1], rgba[i + 2]),
        ),
      );
    }
  }
  const extent = image.getExtent();
  cached = {
    width,
    height,
    extent,
    // map units per sampled pixel
    unit: (step * image.getResolution()) / image.getPixelRatio(),
    key: extent.join(",") + "|" + width + "x" + height,
    xs: Uint16Array.from(xs),
    ys: Uint16Array.from(ys),
    alphas: Uint8Array.from(alphas),
    bands: Uint8Array.from(bands),
  };
  roadPixelsCache.set(image, cached);
  return cached;
}

// One stripe buffer per image geometry. All 12 directions are normally
// requested for the same view and share one; mid-reload they can briefly differ.
function getBuffer(buffers, pixels) {
  let buffer = buffers.get(pixels.key);
  if (!buffer) {
    const canvas = document.createElement("canvas");
    canvas.width = pixels.width;
    canvas.height = pixels.height;
    const imageData = new ImageData(pixels.width, pixels.height);
    buffer = {
      pixels,
      canvas,
      imageData,
      data32: new Uint32Array(imageData.data.buffer),
    };
    buffers.set(pixels.key, buffer);
  }
  return buffer;
}

function paintStripes(buffer, pixels, frameState, heading) {
  const T = frameState.coordinateToPixelTransform;
  const { kx, ky } = stripeAxis(T, heading);
  const unit = pixels.unit;
  // Along-heading distance of sampled pixel (x, y) = s0 + x * sx + y * sy.
  const s0 =
    kx * (pixels.extent[0] + unit / 2) + ky * (pixels.extent[3] - unit / 2);
  const sx = kx * unit;
  const sy = -ky * unit;
  const timeSec = frameState.time / 1000;
  const bandShift = BAND_PX_PER_SEC.map((pxPerSec) =>
    wrap(timeSec * pxPerSec, STRIPE_SPACING),
  );

  const { xs, ys, alphas, bands } = pixels;
  const data32 = buffer.data32;
  const width = pixels.width;
  for (let i = 0; i < xs.length; i++) {
    const s = s0 + xs[i] * sx + ys[i] * sy - bandShift[bands[i]];
    const phase = wrap(s, STRIPE_SPACING);
    const intensity = PROFILE[(phase * PROFILE_SAMPLES_PER_PX) | 0];
    if (intensity === 0) continue;
    const alpha = (intensity * alphas[i]) | 0;
    const idx = ys[i] * width + xs[i];
    // Overlapping directions: keep the brightest stripe (little-endian ABGR).
    if (alpha > data32[idx] >>> 24) {
      data32[idx] = ((alpha << 24) | 0xffffff) >>> 0;
    }
  }
}

// A single overlay layer above the 12 directional layers that draws white
// stripes moving along each direction, at a pace set by each pixel's FCD speed
// band. Motion mode requests the WMS *speed* image for the directional layers,
// so their pixels give both the road mask and the speed.
export function createMotionOverlayLayer(directionalLayers, map, isMotionMode) {
  const canvas = document.createElement("canvas");
  canvas.style.cssText =
    "position:absolute; left:0; top:0; width:100%; height:100%;";
  const context = canvas.getContext("2d");
  const buffers = new Map();

  return new Layer({
    minZoom: 17,
    render: function (frameState) {
      const pixelRatio = frameState.pixelRatio;
      const width = Math.round(frameState.size[0] * pixelRatio);
      const height = Math.round(frameState.size[1] * pixelRatio);
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      } else {
        context.clearRect(0, 0, width, height);
      }
      if (!isMotionMode()) return canvas;

      const used = new Set();
      directionalLayers.forEach(function (layer, id) {
        if (!layer.getVisible() || layer.getOpacity() === 0) return;
        const image = layer.getRenderer().image;
        if (!image || image.getState() !== ImageState.LOADED) return;
        const pixels = getRoadPixels(image);
        if (!pixels) return;
        const buffer = getBuffer(buffers, pixels);
        if (!used.has(buffer)) {
          buffer.data32.fill(0);
          used.add(buffer);
        }
        paintStripes(
          buffer,
          pixels,
          frameState,
          directionHeadingRadians(Number(id)),
        );
      });

      const T = frameState.coordinateToPixelTransform;
      used.forEach(function (buffer) {
        const { extent, unit } = buffer.pixels;
        buffer.canvas.getContext("2d").putImageData(buffer.imageData, 0, 0);
        // sampled pixel -> device px
        context.setTransform(
          pixelRatio * T[0] * unit,
          pixelRatio * T[1] * unit,
          -pixelRatio * T[2] * unit,
          -pixelRatio * T[3] * unit,
          pixelRatio * (T[0] * extent[0] + T[2] * extent[3] + T[4]),
          pixelRatio * (T[1] * extent[0] + T[3] * extent[3] + T[5]),
        );
        context.drawImage(buffer.canvas, 0, 0);
      });
      context.setTransform(1, 0, 0, 1, 0, 0);
      // Drop buffers for geometries no longer on screen.
      buffers.forEach(function (buffer, key) {
        if (!used.has(buffer)) buffers.delete(key);
      });

      if (used.size > 0) map.render();
      return canvas;
    },
  });
}
